
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Diamond, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { useThemeStore } from '@/state/themeStore';
import { usePhantomThemeStore, type WCCOverlayV3 } from '@/stores/phantomThemeStore';
import { supabase } from '@/integrations/supabase/client';
import { useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import BlockchainSelectorDialog from './BlockchainSelectorDialog';
import { useEvmMint } from '@/hooks/useEvmMint';

interface ExportToIpfsButtonProps {
  themeId?: string;
}

const PLACEHOLDER_1PX_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

// Resolve preview image URL from theme layers (cascade fallback)
function resolvePreviewImageUrl(theme: any): string {
  if (theme.lockLayer?.backgroundImage) return theme.lockLayer.backgroundImage;
  if (theme.homeLayer?.backgroundImage) return theme.homeLayer.backgroundImage;
  if (theme.receiveLayer?.centerContainer?.backgroundImage) return theme.receiveLayer.centerContainer.backgroundImage;
  if (theme.sendLayer?.headerContainer?.backgroundImage) return theme.sendLayer.headerContainer.backgroundImage;
  if (theme.buyLayer?.headerContainer?.backgroundImage) return theme.buyLayer.headerContainer.backgroundImage;
  
  console.warn('[ExportToIpfs] No backgroundImage found in theme, using placeholder');
  return PLACEHOLDER_1PX_PNG;
}

// Resolve preview image URL from a Phantom (WCCOverlayV3) theme.
// Returns null if no real background image is present (gradient/color only),
// so the mint flow doesn't push a 1×1 placeholder into IPFS / DB.
function resolvePhantomPreviewImageUrl(pt: WCCOverlayV3 | null | undefined): string | null {
  const url = pt?.global?.background?.url;
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:image/png;base64,iVBORw0KGgo')) return null;
  return url;
}

const ExportToIpfsButton: React.FC<ExportToIpfsButtonProps> = ({ themeId }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastMintTime, setLastMintTime] = useState(0);
  
  const { address, isConnected } = useAppKitAccount();
  const { caipNetwork } = useAppKitNetwork();
  const { mint: mantleMint, isMinting, switchToMantle } = useEvmMint();
  
  // Handle Mantle L2 NFT minting
  const handleMantleMint = async (customName: string) => {
    try {
      setIsExporting(true);
      
      // 1. Switch to Mantle network if needed
      toast.info('Checking Mantle network...');
      const switched = await switchToMantle(true); // testnet
      if (!switched) {
        toast.error('Please switch to Mantle Sepolia network in your wallet');
        return;
      }
      
      // 2. Upload theme to IPFS
      const currentTheme = useThemeStore.getState().theme;
      const themeName = customName || 'Untitled Theme';
      const previewImageUrl = resolvePreviewImageUrl(currentTheme);
      
      toast.info('📤 Uploading theme to IPFS...');
      const { data: ipfsData, error: ipfsError } = await supabase.functions.invoke('upload-to-ipfs', {
        body: {
          themeName,
          themeData: currentTheme,
          previewImageUrl,
          description: 'Custom wallet theme on Mantle L2'
        }
      });
      
      if (ipfsError || !ipfsData?.success) {
        throw new Error(ipfsData?.message || 'Failed to upload to IPFS');
      }
      
      toast.success('✅ Uploaded to IPFS!');
      console.log('[MantleMint] IPFS URI:', ipfsData.metadataUri);
      
      // 3. Mint on Mantle
      const result = await mantleMint(ipfsData.metadataUri, themeName, previewImageUrl);
      
      if (result.success) {
        toast.success(`🎉 NFT Minted on Mantle! TX: ${result.txHash?.slice(0, 10)}...`, { duration: 10000 });
        
        if (result.explorerUrl) {
          console.log('[MantleMint] Explorer:', result.explorerUrl);
        }
      }
      
    } catch (error) {
      console.error('[MantleMint] Failed:', error);
      toast.error(error instanceof Error ? error.message : 'Mantle minting failed');
    } finally {
      setIsExporting(false);
    }
  };
  
  const handleSelectBlockchain = async (blockchain: 'ETH' | 'SOL' | 'MNT', customName: string) => {
    if (isExporting) return;
    
    const now = Date.now();
    if (now - lastMintTime < 5000) {
      toast.error('⏳ Please wait 5 seconds between mints');
      return;
    }
    
    setLastMintTime(now);
    setDialogOpen(false);
    
    if (!isConnected || !address) {
      toast.error('Please connect a wallet using the top-right button');
      return;
    }

    // Handle Mantle L2 minting
    if (blockchain === 'MNT') {
      await handleMantleMint(customName);
      return;
    }
    
    // ETH not supported yet
    if (blockchain === 'ETH') {
      toast.error('Ethereum minting coming soon!');
      return;
    }
    
    // SOL minting (existing flow)
    const networkId = String(caipNetwork?.id || '');
    const isSolana = networkId.includes('solana') || address.length === 44;
    
    if (!isSolana) {
      toast.error('Please switch to a Solana network (Devnet/Mainnet)');
      return;
    }
    
    try {
      setIsExporting(true);
      toast.info('Starting NFT mint process...');
      
      // Step 1: Detect skin kind UPFRONT and pick the matching theme + preview image
      const phantomTheme = usePhantomThemeStore.getState().phantomTheme;
      const wccTheme = useThemeStore.getState().theme;
      const skinKind: 'wcc' | 'phantom' = phantomTheme ? 'phantom' : 'wcc';

      const themeForMint: any = skinKind === 'phantom' ? phantomTheme : wccTheme;
      const themeName = customName || 'Untitled Theme';

      // Step 2: Resolve preview image URL from the CORRECT store/structure
      const previewImageUrl: string | null =
        skinKind === 'phantom'
          ? resolvePhantomPreviewImageUrl(phantomTheme)
          : resolvePreviewImageUrl(wccTheme);

      console.log('[ExportToIpfs] skinKind:', skinKind);
      console.log('[ExportToIpfs] Theme:', themeName);
      console.log('[ExportToIpfs] Preview URL:', previewImageUrl ? previewImageUrl.slice(0, 120) : '(none)');
      console.log('[ExportToIpfs] Theme data size:', JSON.stringify(themeForMint).length);
      
      // Step 2.5: Check user balance (need at least 0.105 SOL)
      const { Connection, clusterApiUrl, PublicKey, LAMPORTS_PER_SOL } = await import('@solana/web3.js');
      const connection = new Connection(clusterApiUrl('devnet'), 'confirmed');
      const userPubkey = new PublicKey(address);
      const balance = await connection.getBalance(userPubkey);
      const balanceSOL = balance / LAMPORTS_PER_SOL;
      
      console.log('[ExportToIpfs] User balance:', balanceSOL.toFixed(4), 'SOL');
      
      if (balance < 0.105 * LAMPORTS_PER_SOL) {
        toast.error(`Insufficient balance. You need at least 0.105 SOL (you have ${balanceSOL.toFixed(4)} SOL). Get devnet SOL from https://faucet.solana.com/`);
        return;
      }
      
      // Step 3: Upload to IPFS via Edge Function
      toast.info('📤 Uploading theme to IPFS...');
      const { data: ipfsData, error: ipfsError } = await supabase.functions.invoke('upload-to-ipfs', {
        body: {
          themeName,
          themeData: themeForMint,
          previewImageUrl, // may be null — edge function will skip image upload gracefully
          skinKind,
          description:
            skinKind === 'phantom'
              ? 'Phantom-native skin minted via Wallet Coast Customs'
              : 'Custom wallet theme created with Wallet Coast Customs'
        }
      });
      
      if (ipfsError || !ipfsData?.success) {
        throw new Error(ipfsData?.message || ipfsError?.message || 'Failed to upload to IPFS');
      }
      
      console.log('[ExportToIpfs] ✅ IPFS Upload success:', {
        metadataUri: ipfsData.metadataUri,
        imageCid: ipfsData.imageCid,
        themeCid: ipfsData.themeCid
      });
      
      toast.success('✅ Uploaded to IPFS!');
      
      // Step 4: Show cost to user
      toast.info('💰 Mint cost: 0.1 SOL + gas fees (~0.005 SOL)', { duration: 5000 });
      
      // Step 5: Build unsigned transaction via Edge Function
      toast.info('🔨 Building mint transaction...');
      console.log('[MintFlow] 1️⃣ Calling mint-nft-build...');

      const { data: buildData, error: buildError } = await supabase.functions.invoke('mint-nft-build', {
        body: {
          userPublicKey: address,
          metadataUri: ipfsData.metadataUri,
          name: `WCC: ${themeName}`,
          symbol: 'WCC',
        }
      });

      if (buildError || !buildData?.success) {
        throw new Error(buildData?.message || buildError?.message || 'Failed to build transaction');
      }

      console.log('[MintFlow] ✅ Transaction built:', {
        mintAddress: buildData.mintAddress,
        txSize: buildData.txBase64?.length
      });

      // Step 6: Deserialize transaction
      const { Transaction } = await import('@solana/web3.js');
      const tx = Transaction.from(Buffer.from(buildData.txBase64, 'base64'));

      // Step 7: Sign transaction via connected wallet (universal check)
      console.log('[MintFlow] 2️⃣ Requesting wallet signature...');
      toast.info('🔓 Please sign the transaction in your wallet...');

      // Universal wallet detection (Phantom, Solflare, Backpack, etc.)
      const wallet = (window as any).solana || (window as any).phantom?.solana || (window as any).solflare;
      
      if (!wallet?.signTransaction) {
        throw new Error('No Solana wallet detected. Please install Phantom, Solflare, or another Solana wallet extension.');
      }

      console.log('[MintFlow] 🔍 Detected wallet:', wallet.isPhantom ? 'Phantom' : wallet.isSolflare ? 'Solflare' : 'Unknown');

      // Ensure wallet is connected
      if (!wallet.isConnected) {
        console.log('[MintFlow] Wallet not connected, requesting connection...');
        try {
          await wallet.connect();
        } catch (err) {
          throw new Error('Wallet connection rejected by user');
        }
      }

      let signed;
      try {
        signed = await wallet.signTransaction(tx);
        console.log('[MintFlow] ✅ Transaction signed by user');
      } catch (err: any) {
        if (err.message?.includes('User rejected') || err.code === 4001) {
          throw new Error('Transaction cancelled by user');
        }
        throw err;
      }

      // Step 8: Send to Solana devnet (reuse connection from balance check)

      toast.info('📤 Sending transaction to Solana...');
      const signature = await connection.sendRawTransaction(signed.serialize(), {
        skipPreflight: true,  // Skip simulation - memo makes each tx unique
        preflightCommitment: 'confirmed'
      });

      console.log('[MintFlow] 3️⃣ Transaction sent:', signature);

      // Step 8.5: Save to database with is_verified: false (will verify after confirmation)
      console.log('[MintFlow] 💾 Saving mint record to database (unverified)...');
      let insertedRecordId: string | null = null;
      console.log('[MintFlow] 🏷️ Detected skin_kind:', skinKind);
      try {
        const { data: insertData, error: insertError } = await supabase
          .from('minted_themes')
          .insert({
            tx_sig: signature,
            mint_address: buildData.mintAddress,
            owner_address: address,
            metadata_uri: ipfsData.metadataUri,
            theme_name: themeName,
            // Prefer the IPFS-hosted image returned by the edge function;
            // fall back to the raw preview URL only if it's a real image (not a placeholder).
            image_url: ipfsData.imageUrl ?? (previewImageUrl ?? null),
            network: 'devnet',
            blockchain: 'solana',
            is_verified: false,  // Will be set to true after confirmation
            skin_kind: skinKind,
            // Store the full WCCOverlayV3 theme so useNftOwnership can
            // serve the overlay back when the owner connects their wallet.
            theme_data: (skinKind === 'phantom' ? phantomTheme : null) as any,
            collection_name: 'WCC'
          })
          .select('id')
          .single();
        
        if (insertError) {
          // Check if duplicate mint_address (already minted)
          if (insertError.code === '23505') {
            console.log('[MintFlow] ⚠️ Duplicate mint_address, skipping insert');
          } else {
            console.error('[MintFlow] ⚠️ Failed to save mint record:', insertError);
          }
        } else {
          insertedRecordId = insertData?.id || null;
          console.log('[MintFlow] ✅ Mint record saved (pending verification)');
        }
      } catch (dbError) {
        console.error('[MintFlow] ⚠️ Database error:', dbError);
      }

      // Step 9: Confirm transaction
      toast.info('⏳ Confirming transaction...');
      let confirmed = false;
      try {
        await connection.confirmTransaction({
          signature,
          blockhash: buildData.recentBlockhash,
          lastValidBlockHeight: buildData.lastValidBlockHeight
        }, 'confirmed');
        confirmed = true;
        console.log('[MintFlow] ✅ Transaction confirmed!', {
          signature,
          explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
        });
      } catch (confirmError) {
        console.warn('[MintFlow] ⚠️ confirmTransaction timed out, falling back to RPC polling:', confirmError);
        toast.info('⏳ Confirmation slow — polling Solana for status...');
        // Fallback: poll signature statuses up to 10 × 3s
        for (let i = 0; i < 10; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const { value } = await connection.getSignatureStatuses([signature], {
              searchTransactionHistory: true
            });
            const st = value?.[0];
            if (st && !st.err && (st.confirmationStatus === 'confirmed' || st.confirmationStatus === 'finalized')) {
              confirmed = true;
              console.log('[MintFlow] ✅ Confirmed via polling on attempt', i + 1);
              break;
            }
          } catch (pollErr) {
            console.warn('[MintFlow] poll attempt failed:', pollErr);
          }
        }
      }

      if (confirmed) {
        // Mark as verified in database
        const { error: updateError } = await supabase
          .from('minted_themes')
          .update({ is_verified: true })
          .eq('mint_address', buildData.mintAddress);

        if (updateError) {
          console.error('[MintFlow] ⚠️ Failed to verify mint record:', updateError);
        } else {
          console.log('[MintFlow] ✅ Mint record verified in database');
          toast.success('✅ NFT verified and added to gallery!');
        }
      } else {
        console.warn('[MintFlow] ⚠️ Could not confirm tx after polling. Gallery self-heal will retry.');
        toast.warning('⚠️ Confirmation timed out. The gallery will auto-verify shortly.');
      }

      // Step 11: Show success
      const explorerUrl = `https://explorer.solana.com/tx/${signature}?cluster=devnet`;
      const mintExplorerUrl = `https://explorer.solana.com/address/${buildData.mintAddress}?cluster=devnet`;

      console.log('[MintFlow] 🎫 Mint address:', buildData.mintAddress);
      console.log('[MintFlow] 🔗 Explorer:', explorerUrl);

      toast.success(
        `🎉 NFT Minted Successfully!\nMint: ${buildData.mintAddress.slice(0, 8)}...\n💰 Cost: 0.1 SOL + gas fees\n🔗 View in Explorer`,
        { duration: 10000 }
      );
      
      setDialogOpen(false);
      
    } catch (error) {
      console.error('❌ Mint process failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Minting failed';
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setIsExporting(false);
    }
  };
  
  const cooldownRemaining = Math.max(0, 5 - Math.floor((Date.now() - lastMintTime) / 1000));
  const isOnCooldown = cooldownRemaining > 0 && !isExporting;

  return (
    <>
      <Button
        onClick={() => setDialogOpen(true)}
        disabled={isExporting || !themeId || isOnCooldown}
        className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white shadow-lg hover:shadow-xl transition-all"
        size="lg"
      >
        {isExporting ? (
          <>
            <Loader className="mr-2 animate-spin" />
            Preparing…
          </>
        ) : isOnCooldown ? (
          <>⏳ Wait {cooldownRemaining}s</>
        ) : (
          <>
            <Diamond className="mr-2" />
            Mint as NFT
          </>
        )}
      </Button>
      
      <BlockchainSelectorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSelectBlockchain={handleSelectBlockchain}
      />
    </>
  );
};

export default ExportToIpfsButton;
